import React, { useMemo, useState } from 'react';
import { Box } from '../../Box/Box';
import { Tooltip } from '../../Tooltip/Tooltip';
import { Text } from '../../Text/Text';
import { SERIES_COLORS } from '../charts.constants';

interface TooltipContentProps {
  label: string;
  value: string;
  color: string;
}

const TooltipContent: React.FC<TooltipContentProps> = ({ label, value, color }) => {
  return (
    <Box display="flex" alignItems="center">
      <Box
        width={8}
        height={8}
        bg={color}
        borderRadius="full"
        mr="4"
        border="1px solid"
        borderColor="bg"
      />

      <Text color="neutral.100" fontSize="small" mr="4">
        {`${label}:`}
      </Text>

      <Text color="neutral.100" fontSize="small" fontWeight={600}>
        {value}
      </Text>
    </Box>
  );
};

interface StackedBarPoint {
  label: string;
  value: number;
  color?: string;
  id?: string;
}

export type StackedBarData = StackedBarPoint[];

interface Props {
  data: StackedBarData;
  height?: number;
  formatter?(val: number): string;
}

export const StackedBar: React.FC<Props> = ({ data, height = 32, formatter }) => {
  const [hovered, setHovered] = useState<number | null>(null);

  const total = useMemo(() => {
    return data.reduce((acc, { value }) => acc + value, 0);
  }, [data]);

  return (
    <Box
      display="flex"
      flexDirection="row"
      height={height}
      width="100%"
      borderRadius="medium"
      css={{ overflow: 'hidden' }}
    >
      {data
        .filter(({ value }) => value !== 0)
        .map(({ id, label, value, color }, index) => {
          return (
            <Tooltip
              key={id || label}
              content={
                <TooltipContent
                  label={label}
                  value={formatter ? formatter(value) : value.toString()}
                  color={color || SERIES_COLORS[index % SERIES_COLORS.length]}
                />
              }
              placement="top"
              variant="chart"
            >
              <Box
                bg={color || SERIES_COLORS[index % SERIES_COLORS.length]}
                flex={value / total}
                height={height}
                onMouseEnter={() => setHovered(index)}
                onMouseLeave={() => setHovered(null)}
                ml={index !== 0 ? 1 : 0}
                css={`
                  opacity: ${hovered !== null && hovered !== index ? 0.3 : 1};
                `}
              />
            </Tooltip>
          );
        })}
    </Box>
  );
};
